
import React, { createContext, useContext, useMemo, useCallback, useState } from 'react';
import { Product } from '@socialflows/shared-lib/src/entities/product.entity';
import {
  useWishlist as useWishlistQuery,
  useAddToWishlist,
  useRemoveFromWishlist,
  useClearWishlist,
} from '@socialflows/shared-lib';
import { useAuth } from './auth-context';
import { useToast } from '@/hooks/use-toast';

type WishlistContextType = {
  wishlistItems: Product[];
  wishlistCount: number;
  isLoading: boolean;
  isInWishlist: (productId: string) => boolean;
  isWishlistLoading: (productId: string) => boolean;
  addToWishlist: (product: Product) => Promise<void>;
  removeFromWishlist: (productId: string) => Promise<void>;
  toggleWishlist: (product: Product) => Promise<void>;
  clearWishlist: () => Promise<void>;
  refreshWishlist: () => Promise<void>;
};

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  const { data: wishlistData, isLoading, refetch } = useWishlistQuery();
  const addToWishlistMutation = useAddToWishlist();
  const removeFromWishlistMutation = useRemoveFromWishlist();
  const clearWishlistMutation = useClearWishlist();

  const wishlistItems = useMemo(
    () =>
      isAuthenticated
        ? (((wishlistData as unknown as Record<string, unknown>)?.products ||
            (Array.isArray(wishlistData) ? wishlistData : [])) as Product[])
        : [],
    [isAuthenticated, wishlistData]
  );

  const isInWishlist = useCallback(
    (productId: string): boolean => {
      return wishlistItems.some((item) => item.id === productId);
    },
    [wishlistItems]
  );

  const isWishlistLoading = useCallback(
    (productId: string): boolean => {
      return loadingIds.has(productId);
    },
    [loadingIds]
  );

  const addToWishlist = useCallback(
    async (product: Product) => {
      if (!isAuthenticated) {
        toast({
          title: 'Authentication Required',
          description: 'Please login to add items to your wishlist',
          variant: 'destructive',
        });
        return;
      }

      setLoadingIds((prev) => new Set(prev).add(product.id));
      try {
        await addToWishlistMutation.mutateAsync(product);
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to add item to wishlist. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(product.id);
          return next;
        });
      }
    },
    [isAuthenticated, toast, addToWishlistMutation]
  );

  const removeFromWishlist = useCallback(
    async (productId: string) => {
      setLoadingIds((prev) => new Set(prev).add(productId));
      try {
        await removeFromWishlistMutation.mutateAsync(productId);
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to remove item from wishlist. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
      }
    },
    [toast, removeFromWishlistMutation]
  );

  const toggleWishlist = useCallback(
    async (product: Product) => {
      if (isInWishlist(product.id)) {
        await removeFromWishlist(product.id);
      } else {
        await addToWishlist(product);
      }
    },
    [isInWishlist, removeFromWishlist, addToWishlist]
  );

  const clearWishlist = useCallback(async () => {
    try {
      await clearWishlistMutation.mutateAsync();
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to clear wishlist. Please try again.',
        variant: 'destructive',
      });
    }
  }, [toast, clearWishlistMutation]);

  const refreshWishlist = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo(
    () => ({
      wishlistItems,
      wishlistCount: wishlistItems.length,
      isLoading: isLoading || authLoading,
      isInWishlist,
      isWishlistLoading,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      clearWishlist,
      refreshWishlist,
    }),
    [
      wishlistItems,
      isLoading,
      authLoading,
      isInWishlist,
      isWishlistLoading,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      clearWishlist,
      refreshWishlist,
    ]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
